import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Wallet as WalletIcon, ArrowDownToLine, TrendingUp, Clock, CheckCircle2, XCircle, X, Loader2, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inr = (n) => `\u20b9${Number(n || 0).toLocaleString('en-IN')}`;

const statusStyle = (s) => {
  switch (s) {
    case 'Approved': return 'bg-blue-100 text-blue-700';
    case 'Paid': return 'bg-green-100 text-green-700';
    case 'Rejected': return 'bg-red-100 text-red-700';
    default: return 'bg-amber-100 text-amber-700';
  }
};

const Wallet = ({ userId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('UPI');
  const [payoutDetails, setPayoutDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchWallet = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await axios.get(`${API}/wallet/${userId}`);
      setData(res.data);
    } catch (e) {
      console.error('wallet fetch error', e);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  const wallet = data?.wallet || {};
  const available = data?.available || 0;
  const minW = wallet.minWithdrawal || 500;

  const handleWithdraw = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast({ title: 'Enter a valid amount', variant: 'destructive' });
    if (amt < minW) return toast({ title: `Minimum withdrawal is ${inr(minW)}`, variant: 'destructive' });
    if (amt > available) return toast({ title: `You can withdraw up to ${inr(available)}`, variant: 'destructive' });
    if (!payoutDetails.trim()) return toast({ title: 'Enter your UPI ID / account details', variant: 'destructive' });
    setSubmitting(true);
    try {
      await axios.post(`${API}/wallet/${userId}/withdraw`, { amount: amt, method, payoutDetails: payoutDetails.trim() });
      toast({ title: 'Withdrawal requested', description: 'Our team will process it shortly.' });
      setShowModal(false); setAmount(''); setPayoutDetails('');
      fetchWallet();
    } catch (e) {
      toast({ title: 'Could not request withdrawal', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading wallet...</div>;
  }

  return (
    <div className="max-w-4xl mx-auto space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><WalletIcon className="w-6 h-6 text-blue-600" /> My Wallet</h1>
        <p className="text-slate-500 text-sm">Your profit is credited here. Withdraw anytime above the minimum limit.</p>
      </div>

      {/* Balance cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-2xl p-5 bg-gradient-to-br from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30">
          <p className="text-blue-100 text-sm">Withdrawable Profit</p>
          <p className="text-3xl font-extrabold mt-1">{inr(available)}</p>
          <Button onClick={() => setShowModal(true)} data-testid="withdraw-btn"
            className="mt-4 w-full bg-white text-blue-700 hover:bg-blue-50 font-bold">
            <ArrowDownToLine className="w-4 h-4 mr-2" /> Withdraw
          </Button>
        </div>
        <div className="rounded-2xl p-5 bg-white border border-slate-200 shadow-sm">
          <p className="text-slate-500 text-sm flex items-center gap-1"><TrendingUp className="w-4 h-4 text-green-500" /> Total Earned</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{inr(wallet.totalCredited)}</p>
          <p className="text-xs text-slate-400 mt-2">Total withdrawn: {inr(wallet.totalWithdrawn)}</p>
        </div>
        <div className="rounded-2xl p-5 bg-white border border-slate-200 shadow-sm">
          <p className="text-slate-500 text-sm flex items-center gap-1"><Clock className="w-4 h-4 text-amber-500" /> Pending Withdrawals</p>
          <p className="text-2xl font-bold text-slate-800 mt-1">{inr(data?.pendingWithdrawals)}</p>
          <p className="text-xs text-slate-400 mt-2">Min withdrawal: {inr(minW)}</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-xs text-blue-700 flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>You can withdraw only your <b>profit</b>. Invested money (ads budget &amp; store fees) is not withdrawable.</span>
      </div>

      {/* Withdrawals */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-800">Withdrawal Requests</div>
        {(data?.withdrawals || []).length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">No withdrawal requests yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.withdrawals.map((w) => (
              <div key={w.id} className="px-4 py-3 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-slate-800">{inr(w.amount)}</p>
                  <p className="text-xs text-slate-400">{w.method} \u2022 {w.payoutDetails} \u2022 {new Date(w.requestedAt).toLocaleDateString()}</p>
                  {w.adminNote && <p className="text-xs text-slate-500 mt-0.5">Note: {w.adminNote}</p>}
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusStyle(w.status)}`}>{w.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Transactions */}
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 font-semibold text-slate-800">Transaction History</div>
        {(data?.transactions || []).length === 0 ? (
          <div className="p-6 text-center text-slate-400 text-sm">No transactions yet.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {data.transactions.map((t) => (
              <div key={t.id} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {t.type === 'credit' ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <XCircle className="w-4 h-4 text-slate-400" />}
                  <div>
                    <p className="text-sm text-slate-700 capitalize">{t.note || t.type}</p>
                    <p className="text-xs text-slate-400">{new Date(t.createdAt).toLocaleString()}</p>
                  </div>
                </div>
                <span className={`font-semibold ${t.type === 'credit' ? 'text-green-600' : 'text-red-500'}`}>
                  {t.type === 'credit' ? '+' : '-'}{inr(t.amount)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Withdraw modal (plain overlay) */}
      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <button onClick={() => setShowModal(false)} className="absolute top-3 right-3 text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            <h3 className="text-xl font-bold text-slate-800 mb-1">Withdraw Profit</h3>
            <p className="text-sm text-slate-500 mb-4">Available: <b className="text-blue-600">{inr(available)}</b> \u2022 Min {inr(minW)}</p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-500">Amount (\u20b9)</label>
                <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={String(minW)} data-testid="withdraw-amount"
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900" />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">Method</label>
                <select value={method} onChange={(e) => setMethod(e.target.value)}
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900 bg-white">
                  <option>UPI</option>
                  <option>Bank Transfer</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500">{method === 'UPI' ? 'UPI ID' : 'Account details (A/c no, IFSC)'}</label>
                <input value={payoutDetails} onChange={(e) => setPayoutDetails(e.target.value)} placeholder={method === 'UPI' ? 'yourname@upi' : 'A/c no & IFSC'} data-testid="withdraw-details"
                  className="w-full mt-1 px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-slate-900" />
              </div>
              <Button onClick={handleWithdraw} disabled={submitting} data-testid="withdraw-submit"
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Request Withdrawal'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Wallet;
